import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCompanyInvites } from '@/hooks/useCompanyInvites';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Loader2, Building2, Mail, Copy, XCircle, UserPlus, Check, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { APP_CONFIG } from '@/config/app';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { EmailSendingOverviewCard } from '@/components/superadmin/EmailSendingOverviewCard';
import { CtmAgencyCard } from '@/components/superadmin/CtmAgencyCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SUPPORTED_COUNTRIES, resolveCompanyLocale } from '@/utils/locale';
import { COMMON_TIMEZONES } from '@/data/timezones';

interface ManualAcceptDialogProps {
  invite: {
    id: string;
    email: string;
    company_name: string;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

function ManualAcceptDialog({ invite, open, onOpenChange, onSuccess }: ManualAcceptDialogProps) {
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('TempPass123!');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    user?: { email: string };
    temporaryPassword?: string;
    error?: string;
  } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invite) return;

    setIsSubmitting(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('accept-company-invite-manually', {
        body: {
          companyInviteId: invite.id,
          fullName,
          temporaryPassword: password,
        },
      });

      if (error) throw error;

      if (data.success) {
        setResult({
          success: true,
          user: data.user,
          temporaryPassword: data.temporaryPassword,
        });
        toast.success('Account created successfully');
        onSuccess();
      } else {
        throw new Error(data.error || 'Failed to create account');
      }
    } catch (err: any) {
      console.error('Manual accept error:', err);
      setResult({
        success: false,
        error: err.message || 'Failed to create account',
      });
      toast.error(err.message || 'Failed to create account');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFullName('');
    setPassword('TempPass123!');
    setResult(null);
    onOpenChange(false);
  };

  const copyCredentials = () => {
    if (result?.user && result?.temporaryPassword) {
      const text = `Email: ${result.user.email}\nPassword: ${result.temporaryPassword}`;
      navigator.clipboard.writeText(text);
      toast.success('Credentials copied to clipboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Create Account Manually
          </DialogTitle>
          <DialogDescription>
            Create a founder account for {invite?.company_name}
          </DialogDescription>
        </DialogHeader>

        {result?.success ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-center gap-2 text-primary">
                <Check className="h-5 w-5" />
                <span className="font-medium">Account created successfully!</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Credentials</Label>
              <div className="rounded-md bg-muted p-3 font-mono text-sm">
                <p><strong>Email:</strong> {result.user?.email}</p>
                <p><strong>Password:</strong> {result.temporaryPassword}</p>
              </div>
              <Button variant="outline" size="sm" onClick={copyCredentials} className="w-full">
                <Copy className="mr-2 h-4 w-4" />
                Copy Credentials
              </Button>
            </div>

            <DialogFooter>
              <Button onClick={handleClose}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={invite?.email || ''}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                placeholder="John Smith"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Temporary Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                The user should change this after first login
              </p>
            </div>

            {result?.error && (
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">
                {result.error}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || !fullName}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Account
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function SuperAdmin() {
  const { isSuperAdmin, isLoading: checkingAccess } = useSuperAdmin();
  const { companyInvites, isLoading, createCompanyInvite, revokeCompanyInvite, refetch } = useCompanyInvites();
  
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [abn, setAbn] = useState('');
  const [phone, setPhone] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postcode, setPostcode] = useState('');
  // Country drives currency, tax defaults, address labels and timezone for the new tenant.
  const [country, setCountry] = useState('Australia');
  const [timezone, setTimezone] = useState('Australia/Sydney');
  const inviteLocale = resolveCompanyLocale(country);

  const handleCountryChange = (name: string) => {
    setCountry(name);
    setTimezone(resolveCompanyLocale(name).defaultTimezone);
  };

  // Manual accept dialog state
  const [manualAcceptInvite, setManualAcceptInvite] = useState<{
    id: string;
    email: string;
    company_name: string;
  } | null>(null);

  if (checkingAccess) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Access Denied</CardTitle>
          <CardDescription>You do not have super admin privileges</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();

    await createCompanyInvite.mutateAsync({
      email,
      companyName,
      companyDetails: {
        abn: abn ? abn.replace(/\s/g, '') : undefined,
        phone: phone || undefined,
        email: companyEmail || undefined,
        street_address: address || undefined,
        suburb: city || undefined,
        state: state || undefined,
        postcode: postcode || undefined,
        country,
        timezone,
      },
    });

    // Reset form
    setEmail('');
    setCompanyName('');
    setAbn('');
    setPhone('');
    setCompanyEmail('');
    setAddress('');
    setCity('');
    setState('');
    setPostcode('');
    handleCountryChange('Australia');
  };

  const copyInviteUrl = (token: string) => {
    const url = `${APP_CONFIG.appUrl}/company-invite/accept?token=${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Invite URL copied to clipboard');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Invite Company Founder
          </CardTitle>
          <CardDescription>
            Create a new company and invite the founder to set up their account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="email">Founder Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="founder@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name *</Label>
                <Input
                  id="companyName"
                  placeholder="Acme Corp"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="country">Country *</Label>
                <Select value={country} onValueChange={handleCountryChange}>
                  <SelectTrigger id="country">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPORTED_COUNTRIES.map((c) => (
                      <SelectItem key={c.countryCode} value={c.name}>
                        {c.name} ({c.currency})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Sets currency, tax defaults ({inviteLocale.taxDefaults.label}
                  {inviteLocale.taxDefaults.enabled ? ` ${inviteLocale.taxDefaults.rate}%` : ' off'}), address format and postcodes.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger id="timezone">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMMON_TIMEZONES.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label} ({tz.offset})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="abn">{inviteLocale.taxIdLabels.abn}</Label>
                <Input
                  id="abn"
                  placeholder={inviteLocale.taxIdPlaceholder}
                  value={abn}
                  onChange={(e) => setAbn(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder={inviteLocale.phonePlaceholder}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyEmail">Company Email</Label>
              <Input
                id="companyEmail"
                type="email"
                placeholder="info@company.com"
                value={companyEmail}
                onChange={(e) => setCompanyEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Street Address</Label>
              <Textarea
                id="address"
                placeholder="123 Main Street"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={2}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="city">{inviteLocale.addressLabels.locality}</Label>
                <Input
                  id="city"
                  placeholder={inviteLocale.addressPlaceholders.locality}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">{inviteLocale.addressLabels.region}</Label>
                <Input
                  id="state"
                  placeholder={inviteLocale.addressPlaceholders.region}
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">{inviteLocale.addressLabels.postcode}</Label>
                <Input
                  id="postcode"
                  placeholder={inviteLocale.postcode.placeholder}
                  maxLength={inviteLocale.postcode.maxLength}
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                />
              </div>
            </div>

            <Button type="submit" disabled={createCompanyInvite.isPending}>
              {createCompanyInvite.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Mail className="mr-2 h-4 w-4" />
              Send Invite
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Company Invitations</CardTitle>
          <CardDescription>Manage pending and completed company founder invitations</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : companyInvites.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No company invitations yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companyInvites.map((invite) => (
                  <TableRow key={invite.id}>
                    <TableCell className="font-medium">{invite.email}</TableCell>
                    <TableCell>{invite.company_name}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          invite.status === 'accepted'
                            ? 'default'
                            : invite.status === 'pending'
                            ? 'secondary'
                            : 'destructive'
                        }
                      >
                        {invite.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{format(new Date(invite.created_at), 'PP')}</TableCell>
                    <TableCell>{format(new Date(invite.expires_at), 'PP')}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {invite.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyInviteUrl(invite.token)}
                              title="Copy invite URL"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setManualAcceptInvite({
                                id: invite.id,
                                email: invite.email,
                                company_name: invite.company_name,
                              })}
                              title="Create account manually"
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => revokeCompanyInvite.mutate(invite.id)}
                              disabled={revokeCompanyInvite.isPending}
                              title="Revoke invitation"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <EmailSendingOverviewCard enabled={isSuperAdmin} />

      <CtmAgencyCard enabled={isSuperAdmin} />

      <ManualAcceptDialog
        invite={manualAcceptInvite}
        open={!!manualAcceptInvite}
        onOpenChange={(open) => !open && setManualAcceptInvite(null)}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
